// @core/stores/project/ProjectStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  AddProjectMemberRequest,
  ArchiveProjectRequest,
  CloneProjectRequest,
  CreateProjectRequest,
  GetProjectSummaryResponse,
  ListProjectMembersResponse,
  Project,
  ProjectMemberResponse,
  ProjectResponse,
  RestoreProjectRequest,
  UpdateProjectMemberRoleRequest,
  UpdateProjectRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectStore {
  // List state
  items: Project[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Project | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Project>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Project | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Project | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add project member
   */
  async AddProjectMember(project_id: string, data: AddProjectMemberRequest): Promise<ProjectMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.AddProjectMember(project_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Archive project
   */
  async ArchiveProject(project_id: string, data: ArchiveProjectRequest): Promise<ProjectResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.ArchiveProject(project_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Clone project
   */
  async CloneProject(project_id: string, data: CloneProjectRequest): Promise<ProjectResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.CloneProject(project_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create project
   */
  async CreateProject(data: CreateProjectRequest): Promise<Project> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.CreateProject(data);
      const item = response.project;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete project
   */
  async DeleteProject(project_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await projectApi.DeleteProject(project_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== project_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === project_id) {
          this.currentItem = null;
        }

        this.removeFromCache(project_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get project
   */
  async GetProject(project_id: string): Promise<Project> {
    // Check cache first
    const cached = this.cache.get(project_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetProject(project_id);
      const item = response.project;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get project summary
   */
  async GetProjectSummary(project_id: string): Promise<GetProjectSummaryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.GetProjectSummary(project_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List project members
   */
  async ListProjectMembers(project_id: string, params?: PaginatedRequest): Promise<ListProjectMembersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.ListProjectMembers(project_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List projects
   */
  async ListProjects(params?: PaginatedRequest & { member_id?: string | number | boolean; owner_id?: string | number | boolean; parent_id?: string | number | boolean; parent_type?: string | number | boolean; search?: string | number | boolean; status?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListProjects(params);

      runInAction(() => {
        this.items = response.projects?.projects ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove project member
   */
  async RemoveProjectMember(project_id: string, profile_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.RemoveProjectMember(project_id, profile_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Restore archived project
   */
  async RestoreProject(project_id: string, data: RestoreProjectRequest): Promise<ProjectResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.RestoreProject(project_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update project
   */
  async UpdateProject(project_id: string, data: UpdateProjectRequest): Promise<Project> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateProject(project_id, data);
      const item = response.project;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update member role
   */
  async UpdateProjectMemberRole(project_id: string, profile_id: string, data: UpdateProjectMemberRoleRequest): Promise<ProjectMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.UpdateProjectMemberRole(project_id, profile_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const projectStore = new ProjectStore();
